import {type FC, forwardRef, useEffect, useState} from 'react';
import type {
  DropdownIndicatorProps,
  OptionProps,
  SelectInstance,
} from 'react-select';
import ReactSelect, {components} from 'react-select';
import CreatableSelect from 'react-select/creatable';

import styled, {useTheme} from 'styled-components';

import {useI18n} from '@yourcoach/i18n';

import type {
  GetComponentProps,
  WithDomStyleProp,
} from '../../utils/utility-types';
import type {ColorScheme, Props, SelectOption} from './types';

import ArrowDownSvg from '../../assets/icons/secondary/Arrow - Down 2.svg';
import {payloadActionCreator} from '../../utils/events';
import {useForwardedRef} from '../../utils/hooks/useForwardedRef';
import {Icon} from '../Icon';
import {ErrorText} from '../TextInput/styles';
import {Container, menu, option, singleValue} from './styles';
import {SelectV2} from './v2/Select.v2';

/**
 * Below is the ugliest hack ever.
 * Styled component v6 doesn't forward theme prop required by react-select.
 *
 * @see https://github.com/styled-components/styled-components/issues/4182
 */
const ForwardThemeWrapper: FC<{
  Component: FC<any>;
  forwardTheme: any;
}> = ({Component, forwardTheme, ...rest}) => {
  return <Component {...rest} forwardTheme={forwardTheme} />;
};

const ThemeForwardingWrapper: FC<{
  Component: FC<any>;
  forwardTheme: any;
}> = ({Component, forwardTheme, ...rest}) => {
  return <Component {...rest} theme={forwardTheme} />;
};

const ForwardedThemeMenuWrapper = ({forwardTheme, ...props}) => {
  return (
    <ThemeForwardingWrapper
      Component={components.Menu}
      forwardTheme={forwardTheme}
      {...props}
    />
  );
};

const StyledMenu = styled(ForwardedThemeMenuWrapper)`
  && {
    ${menu}
  }
`;

const ForwardedThemeOptionWrapper = ({forwardTheme, ...props}) => {
  return (
    <ThemeForwardingWrapper
      Component={components.Option}
      forwardTheme={forwardTheme}
      {...props}
    />
  );
};

const StyledOption: FC<OptionProps & {scheme: ColorScheme}> = styled(
  ForwardedThemeOptionWrapper,
)`
  && {
    ${
      // @ts-expect-error: TODO: describy why
      option
    }
  }
`;

const ForwardedThemeSingleValueWrapper = ({forwardTheme, ...props}) => {
  return (
    <ThemeForwardingWrapper
      Component={components.SingleValue}
      forwardTheme={forwardTheme}
      {...props}
    />
  );
};

const StyledSingleValue: FC = styled(ForwardedThemeSingleValueWrapper)`
  && {
    ${
      // @ts-expect-error: TODO: describy why
      singleValue
    }
  }
`;

const baseStyles = {
  menuPortal: base => ({...base, zIndex: 9999}),
};

const DropdownIndicator = (props: DropdownIndicatorProps<unknown, true>) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon color={'icon.gray'} size={18} svg={ArrowDownSvg} />
    </components.DropdownIndicator>
  );
};

const SelectV1 = forwardRef<SelectInstance, WithDomStyleProp<Props>>(
  (
    {
      blurInputOnSelect = true,
      error,
      isValidNewOption,
      onChange,
      scheme = 'primary',
      testID,
      useCreatedOptionAsSearch,
      usePortal,
      ...props
    },
    ref,
  ) => {
    const {t} = useI18n();
    const appTheme = useTheme();
    const localRef = useForwardedRef(ref);
    const [inputValue, setInputValue] = useState('');
    const [portalisedProps, setPortalisedProps] = useState<PartialSelectProps>(
      {},
    );

    const SelectComponent = props.isCreatable ? CreatableSelect : ReactSelect;

    type PartialSelectProps = Partial<
      GetComponentProps<typeof SelectComponent>
    >;

    const formatCreateLabel = (value: string) => {
      return props.formatCreateLabel
        ? props.formatCreateLabel(value)
        : t('shared:use_value', {value});
    };

    useEffect(() => {
      setPortalisedProps(
        usePortal
          ? {
              menuPortalTarget:
                typeof document !== 'undefined' ? document.body : null,
              menuPosition: 'fixed',
              menuShouldScrollIntoView: false,
            }
          : {},
      );
    }, [usePortal]);

    const createdOptionAsSearchProps: PartialSelectProps =
      props.isCreatable && useCreatedOptionAsSearch
        ? {
            inputValue: inputValue,
            onBlur: e => {
              props.onBlur?.(e);

              setInputValue('');
            },
            onFocus: e => {
              props.onFocus?.(e);

              const selectedOption: SelectOption | undefined = localRef.current
                ?.state.selectValue[0] as SelectOption;

              setInputValue(selectedOption?.label ?? '');
            },
            onInputChange: value => setInputValue(value),
          }
        : {};

    return (
      <Container className={props.className} error={error} style={props.style}>
        <SelectComponent
          className="react-select-container"
          classNamePrefix="react-select"
          components={{
            DropdownIndicator,
            IndicatorSeparator: () => null,
            Menu: menuProps => {
              return (
                <ForwardThemeWrapper
                  Component={StyledMenu}
                  forwardTheme={menuProps.theme}
                  {...menuProps}
                />
              );
            },
            Option: optionProps => {
              const extendedProps = {
                ...optionProps,
                scheme,
              };

              return (
                <ForwardThemeWrapper
                  Component={StyledOption}
                  forwardTheme={optionProps.theme}
                  {...extendedProps}
                />
              );
            },
            SingleValue: singleValueProps => {
              return (
                <ForwardThemeWrapper
                  Component={StyledSingleValue}
                  forwardTheme={singleValueProps.theme}
                  {...singleValueProps}
                />
              );
            },
          }}
          controlShouldRenderValue={true}
          ref={localRef}
          styles={{
            ...baseStyles,
            placeholder: base => ({
              ...base,
              color: props.placeholderColor ?? base.color,
            }),
          }}
          {...portalisedProps}
          blurInputOnSelect={blurInputOnSelect}
          theme={theme => ({
            ...theme,
            ...appTheme,
            colors: {...theme.colors, ...appTheme.color.v2},
          })}
          {...props}
          {...createdOptionAsSearchProps}
          data-test-id={testID}
          formatCreateLabel={formatCreateLabel}
          isValidNewOption={isValidNewOption}
          onChange={(e: SelectOption) => onChange?.(payloadActionCreator(e))}
        />
        {error ? <ErrorText>{error}</ErrorText> : null}
      </Container>
    );
  },
);

const Select: typeof SelectV1 & {V2: typeof SelectV2} = Object.assign(
  SelectV1,
  {V2: SelectV2},
);

export {Select};
